import * as jQuery from 'jquery';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { Component, ElementRef, HostListener, OnDestroy, Input, OnInit, ViewChild } from '@angular/core';
import { EmitterService } from '../../../../core/utility/services/emitter.service';
import {
	CONSTANTS,
	EVENT_EMITTERS,
	OPTIONS,
	PRINTABLE_ACCESS_KEYS,
	PRISTINE_PERMISSIONS
} from '../../../analytics/constants/analytics.constants';
import { AnalyticsDataModel } from '../../../analytics/models/analytics.datamodel';
import { AnalyticsMapperService } from '../../../analytics/services/analytics.mapper.service';
import { Configuration } from '../../../analytics-core/interfaces/configuration.interface';
import { UtilityService } from '../../../../core/utility/services/utility.service';
import { PERMISSION } from '../../../analytics-core/interfaces/permission.interface';
import { AnalyticsMixpanelService } from '../../../analytics/services/analytics.mixpanel.service';

export interface DropDownSettings {
	multiple: boolean;
	closeOnSelect: boolean;
	clearAllText: string;
	clearable: boolean;
	dropdownPosition: string;
	loading: boolean;
	loadingText: string;
	markFirst: boolean;
	isOpen: boolean;
	maxSelectedItems: number;
	hideSelected: boolean;
	notFoundText: string;
	placeholder: string;
	searchable: boolean;
	selectOnTab: boolean;
	virtualScroll: boolean;
}

@Component({
	selector: 'app-lazy-dropdown',
	styleUrls: ['./lazy-dropdown.component.css'],
	templateUrl: './lazy-dropdown.component.html'
})
export class LazyDropdownComponent implements OnInit, OnDestroy {
	dropdownList = [];
	selectedItems = [];
	dropdownSettings: DropDownSettings;
	content = '';
	checkForAdvertisers: any;

	public type: any;
	public debouncer: Subject<any> = new Subject();
	public campaignLoadedSubscription;
	public selectedCampaignLoadedSubscription;
	public hasDebouncerActive: boolean;
	public selectAll: boolean = true;
	public ADVERTISERS: string = CONSTANTS.ADVERTISERS;
	protected boolSelectedOrRemovedEvent = false;
	protected boolDropdownClosed = false;
	protected boolAdvertiserDropDownClosed: boolean;
	private campaignsRequestSubscription;
	private campaignloaded = false;
	private campaignsRequestCounter;
	private campaignsStatusSubscription: any;
	public configuration: Configuration;
	public hasCustomizeAdvertiserLimit: boolean;
	public maxAdvertisersAllowed: number;
	public productCampaignsActiveCount: any = 0;
	public isR9: boolean = false;
	public isR104: boolean = false;
	public isR8: boolean = false;
	constructor(
		protected analyticsDataModel: AnalyticsDataModel,
		protected analyticsMapperService: AnalyticsMapperService,
		protected analyticsMixpanelService: AnalyticsMixpanelService
	) {
		this.campaignLoadedSubscription = EmitterService.get(EVENT_EMITTERS.CAMPAIGN_LOADED).subscribe((response) => {
			if (response) {
				if (this.type === CONSTANTS.ADVERTISERS) {
					this.campaignloaded = false;
				} else if (this.type === CONSTANTS.CAMPAIGNS) {
					this.getConfigurationByType();
				}
			}
		});

		this.selectedCampaignLoadedSubscription = EmitterService.get(EVENT_EMITTERS.SELECTED_CAMPAIGN_LOADED).subscribe(
			(response: any) => {
				if (this.type === CONSTANTS.ADVERTISERS) {
					if (response) {
						this.campaignloaded = response;
						if (this.campaignsRequestCounter === 0 && !this.hasDebouncerActive && this.boolDropdownClosed) {
							this.refreshData();
						}
					}
				}
			}
		);

		this.campaignsRequestSubscription = EmitterService.get(EVENT_EMITTERS.CAMPAIGN_REQUEST_COUNTER).subscribe(
			(response: any) => {
				this.campaignsRequestCounter = response;
			}
		);

		this.campaignsStatusSubscription = EmitterService.get(EVENT_EMITTERS.REFRESH_CAMPAIGNS).subscribe(
			(response: any) => {
				if (this.type === CONSTANTS.CAMPAIGNS) {
					const selectedCampaigns =
						this.selectedItems.length > 0
							? this.analyticsMapperService.mapCampaignsDropdownSelectionDataToInterface(this.selectedItems)
							: this.analyticsDataModel.selectedCampaigns;
					if (selectedCampaigns.length > 0) {
						this.analyticsDataModel.campaigns = this.analyticsDataModel.campaigns.map((item) => {
							for (const campaign of selectedCampaigns) {
								if (campaign.campaignId === item.campaignId) {
									item.selected = true;
								}
							}
							return item;
						});
					}
					this.getConfigurationByType();
				}
			}
		);
	}

	ngOnInit(): void {
		this.configuration = this.analyticsDataModel.config;
		this.isR9 = this.analyticsDataModel.config.version === 'R9' ? true : false;
		this.isR104 = this.analyticsDataModel.config.directoryName == 'default-r104' ? true : false;
		this.isR8 = this.analyticsDataModel.config.version === 'R8' ? true : false;
		const permissions = this.analyticsDataModel.setupData.userPermissions;
		this.hasCustomizeAdvertiserLimit = !UtilityService.isUndefinedOrNull(
			permissions[PERMISSION[PERMISSION.customizeAdvertiserLimit]]
		)
			? permissions[PERMISSION[PERMISSION.customizeAdvertiserLimit]]
			: false;
		if (this.hasCustomizeAdvertiserLimit) {
			this.maxAdvertisersAllowed = this.configuration.maxAdvertisersAllowed
				? this.configuration.maxAdvertisersAllowed
				: OPTIONS.ADVERTISERS.DEFAULT_SELECTION;
		} else {
			this.maxAdvertisersAllowed = OPTIONS.ADVERTISERS.DEFAULT_SELECTION;
		}
		this.setPropertiesByType();
		this.debouncer.pipe(debounceTime(500)).subscribe((val) => {
			if (this.type === CONSTANTS.ADVERTISERS) {
				this.hasDebouncerActive = false;
				EmitterService.get(EVENT_EMITTERS.ADVERTISERS).emit(this.selectedItems);
			} else if (this.type === CONSTANTS.CAMPAIGNS) {
				const selectedCampaigns = this.analyticsMapperService.mapCampaignsDropdownSelectionDataToInterface(
					this.selectedItems
				);
				EmitterService.get(EVENT_EMITTERS.CAMPAIGNS).emit(selectedCampaigns);
			}
		});
		setTimeout(() => {
			this.getConfigurationByType();
		}, CONSTANTS.LOAD_TIME);
		if (this.type === CONSTANTS.CAMPAIGNS) {
			this.analyticsMixpanelService.setUserProfile(this.analyticsDataModel.mixpanel.u_id);
			this.analyticsMixpanelService.mixpanelData('Analytics / Load Analytics Dashboard');
		}
	}
	onOpen(event: any): void {
		this.analyticsDataModel.userInteracted = true;
		jQuery('.daterangepicker').hide();
	}
	ngOnDestroy(): void {
		if (this.campaignLoadedSubscription) {
			this.campaignLoadedSubscription.unsubscribe();
		}
		if (this.selectedCampaignLoadedSubscription) {
			this.selectedCampaignLoadedSubscription.unsubscribe();
		}
		if (this.campaignsRequestSubscription) {
			this.campaignsRequestSubscription.unsubscribe();
		}
	}
	onAdd(item: any): void {
		this.boolSelectedOrRemovedEvent = true;
		this.campaignloaded = false;
		setTimeout(() => {
			if (this.type === CONSTANTS.ADVERTISERS && this.selectedItems.length > this.maxAdvertisersAllowed) {
				this.campaignloaded = true;
				item.selected = false;
				this.selectedItems = this.selectedItems.filter((data) => data.selected !== false);
			} else {
				item.selected = true;
				if (this.type === CONSTANTS.ADVERTISERS) {
					this.hasDebouncerActive = true;
				} else {
					this.selectAll = this.selectedItems.length === this.dropdownList.length;
				}
				this.debouncer.next(this.type);
			}
		});
	}

	unselectAll(): void {
		this.boolSelectedOrRemovedEvent = true;
		this.campaignloaded = false;
		this.selectedItems = [];
		this.dropdownList = this.dropdownList.map((data) => {
			data.selected = false;
			return data;
		});
		if (this.type === CONSTANTS.ADVERTISERS) {
			this.hasDebouncerActive = true;
		}
		this.analyticsDataModel.selectedCampaigns = [];
		this.debouncer.next(this.type);
	}

	toggleAll(): void {
		this.boolSelectedOrRemovedEvent = true;
		this.campaignloaded = false;
		this.selectedItems = [];
		this.selectAll = !this.selectAll;
		this.dropdownList = this.dropdownList.map((data) => {
			data.selected = this.selectAll;
			return data;
		});
		this.selectedItems = this.dropdownList.filter((data) => {
			return data.selected;
		});
		this.debouncer.next(this.type);
	}

	onRemove(item: any): void {
		this.boolSelectedOrRemovedEvent = true;
		this.campaignloaded = false;
		setTimeout(() => {
			item.value.selected = false;
			if (this.type === CONSTANTS.ADVERTISERS) {
				this.hasDebouncerActive = true;
			} else {
				this.selectAll = this.selectedItems.length === this.dropdownList.length;
			}
			this.debouncer.next(this.type);
		});
	}

	refreshData(): void {
		this.boolSelectedOrRemovedEvent = false;
		this.boolDropdownClosed = false;
		this.campaignloaded = false;
		EmitterService.get(EVENT_EMITTERS.SELECTED_CAMPAIGN_LOADED).emit(false);
		setTimeout(() => {
			EmitterService.get(EVENT_EMITTERS.REFRESH_DATA).emit(true);
		}, 500);

		this.productCampaignsActiveCount = this.analyticsMixpanelService.productSelectedActiveCampaign();
		var event_name =
			this.type == 'advertisers'
				? 'Analytics / Reload Analytics Dashboard - Advertiser Adjustment'
				: 'Analytics / Reload Analytics Dashboard - Campaigns Adjustment';
		this.analyticsMixpanelService.mixpanelProductClickData(event_name, this.productCampaignsActiveCount);
	}

	onClose(event: any): void {
		this.boolDropdownClosed = true;
		switch (this.type) {
			case CONSTANTS.ADVERTISERS:
				if (this.campaignloaded && this.boolSelectedOrRemovedEvent) {
					this.refreshData();
				}
				break;
			case CONSTANTS.CAMPAIGNS:
				if (this.boolSelectedOrRemovedEvent) {
					this.refreshData();
				}
				break;
		}
	}

	setPropertiesByType(): void {
		let placeholder;
		let loadingText;
		let maxSelectedItems;
		let notFoundText;
		if (this.type === CONSTANTS.ADVERTISERS) {
			placeholder = 'Select Advertisers';
			loadingText = 'Loading Advertisers';
			maxSelectedItems = 10;
			notFoundText = 'No Advertisers Found';
		} else if (this.type === CONSTANTS.CAMPAIGNS) {
			placeholder = 'Select Campaigns';
			loadingText = 'Loading Campaigns';
			maxSelectedItems = 10;
			notFoundText = 'No Campaigns Found';
		}
		this.dropdownSettings = {
			// maxSelectedItems: maxSelectedItems,
			clearAllText: 'Clear All',
			clearable: false,
			closeOnSelect: false,
			loading: false,
			loadingText,
			multiple: true,
			notFoundText,
			placeholder,
			searchable: true,
			virtualScroll: true
		} as DropDownSettings;
	}

	getConfigurationByType(): void {
		if (this.type === CONSTANTS.ADVERTISERS) {
			this.dropdownList = this.analyticsDataModel.advertisersListForDropDown;
			this.selectedItems = this.analyticsDataModel.selectedAdvertisersListForDropDown;
			if (this.selectedItems.length > 0) {
				for (const selected of this.selectedItems) {
					for (const dropdownItem of this.dropdownList) {
						if (dropdownItem.id === selected.id) {
							dropdownItem.selected = true;
						}
					}
				}
				this.hasDebouncerActive = true;
				this.debouncer.next(this.type);
			}

			this.content =
				this.isR8 || this.isR104
					? `Select up to ${this.maxAdvertisersAllowed} Advertisers`
					: `Please select up to ${this.maxAdvertisersAllowed} Advertisers`;
		} else if (this.type === CONSTANTS.CAMPAIGNS) {
			this.dropdownList = this.analyticsDataModel.campaignsListForDropDown;
			this.selectedItems = this.analyticsDataModel.selectedCampaignsListForDropDown;
			this.debouncer.next(this.type);
			if (this.dropdownList.length > 0) {
				this.content = 'Select campaigns';
			}
			if (this.dropdownList.length === 0) {
				this.content = '';
			}
			this.selectAll = this.selectedItems.length === this.dropdownList.length;
		}
	}
}
